import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { industries } from '../data/mock';
import * as Icons from 'lucide-react';

const Industries = () => {
  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  const industryDetails = {
    manufacturing: {
      challenges: ["Legacy system integration", "IoT data management", "Supply chain visibility"],
      solutions: ["Industry 4.0 transformation", "Smart factory solutions", "Predictive maintenance"]
    },
    financial: {
      challenges: ["Regulatory compliance", "Legacy modernization", "Real-time processing"],
      solutions: ["Cloud-native banking", "AI-powered risk management", "Automated compliance"]
    },
    retail: {
      challenges: ["Omnichannel integration", "Inventory management", "Customer experience"],
      solutions: ["Unified commerce platform", "Personalization engine", "Real-time inventory"]
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Industry-Specific
            <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Expertise</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Deep domain knowledge combined with cutting-edge technology to solve your industry's unique challenges.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => (
              <Card 
                key={industry.id}
                className="p-8 hover:shadow-2xl transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-orange-200 bg-white"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {getIcon(industry.icon)}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{industry.name}</h3>
                <p className="text-gray-600 leading-relaxed">{industry.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Industry Deep Dive - Manufacturing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Card className="overflow-hidden border-2 border-orange-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-12 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <div className="w-16 h-16 bg-orange-400/20 rounded-2xl flex items-center justify-center mb-6">
                  <Icons.Factory className="w-8 h-8 text-orange-400" />
                </div>
                <h2 className="text-3xl font-bold mb-6">Manufacturing Excellence</h2>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  Transform your manufacturing operations with Industry 4.0 solutions that connect machines, people, and processes.
                </p>
                
                <h3 className="text-xl font-bold mb-4">Key Challenges We Solve:</h3>
                <ul className="space-y-3 mb-8">
                  {industryDetails.manufacturing.challenges.map((challenge, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2.5" />
                      <span className="text-gray-300">{challenge}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-bold mb-4">Our Solutions:</h3>
                <ul className="space-y-3">
                  {industryDetails.manufacturing.solutions.map((solution, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2.5" />
                      <span className="text-gray-300">{solution}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-12 bg-gradient-to-br from-orange-50 to-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Success Story</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  A Fortune 500 manufacturing company reduced operational costs by 40% and improved production efficiency by 60% through our AI-powered integration platform.
                </p>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  {[
                    { metric: "40%", label: "Cost Reduction" },
                    { metric: "60%", label: "Efficiency Gain" },
                    { metric: "99.9%", label: "System Uptime" },
                    { metric: "15", label: "Facilities Connected" }
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center p-4 bg-white rounded-xl shadow-sm">
                      <div className="text-3xl font-bold text-orange-600 mb-1">{stat.metric}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <Link to="/case-studies">
                  <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 rounded-xl">
                    View Full Case Study
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Industry Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Cross-Industry Impact
            </h2>
            <p className="text-xl text-gray-600">
              Proven results across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { industry: "Manufacturing", projects: "120+", avgROI: "3.2x" },
              { industry: "Financial Services", projects: "85+", avgROI: "4.1x" },
              { industry: "Retail", projects: "95+", avgROI: "2.8x" },
              { industry: "Healthcare", projects: "60+", avgROI: "3.5x" }
            ].map((stat, index) => (
              <Card key={index} className="p-8 text-center bg-white hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{stat.industry}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-4xl font-bold text-orange-600 mb-1">{stat.projects}</div>
                    <div className="text-sm text-gray-600">Projects Delivered</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-gray-900 mb-1">{stat.avgROI}</div>
                    <div className="text-sm text-gray-600">Average ROI</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Transform Your Industry with AiFlo
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's explore how our industry expertise can accelerate your digital transformation.
          </p>
          <Link to="/contact">
            <Button className="bg-orange-400 hover:bg-orange-500 text-white px-8 py-6 text-lg rounded-xl shadow-2xl">
              Discuss Your Industry Needs
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;
